<template>
  <div id="add-asset">
    <div class="ContentContainer ContentContainer--WithButton">
      <BackButton />

      <h3 class="heading-lined">
        {{ $t(pageMode === "edit" ? "global.edit" : "exchange.add") }}
        {{ pageMode === "sell" ? $t("exchange.Sell") : "" }}
        {{ $t("profitloss.transaction") }}
        {{ coin ? coin.name : "" }}
      </h3>

      <div v-if="!coin">
        <p>
          {{ $t("add_page.any_asset.1") }}
          <a href="https://coinmarketcap.com" target="_blank">
            Coinmarketcap.com
          </a>
          {{ $t("add_page.any_asset.2") }}
        </p>

        <input
          type="text"
          class="form-control form-control-lg mt-4"
          :placeholder="$t('add_page.search')"
          v-model="query"
          ref="input"
          @keyup.enter="goToFirstIfAvailable"
        />

        <table
          v-if="filteredCoins.length > 0"
          class="table table-striped mt-5"
        >
          <colgroup>
            <col width="8%" />
            <col width="20%" />
          </colgroup>
          <tbody>
            <tr
              v-for="filteredCoin in filteredCoins"
              v-bind:key="filteredCoin.id + filteredCoin.price_usd"
            >
              <td>
                {{ filteredCoin.name }}
                <em>{{ filteredCoin.symbol }}</em>
              </td>
              <td class="text-right" @click.prevent="addCoin(filteredCoin)">
                <button
                  class="btn btn-secondary"
                  @click="selectCoin(filteredCoin)"
                >
                  {{ $t("add_page.next") }}

                  <i class="fa fa-angle-double-right ml-2"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-if="query.length > 0 && filteredCoins.length === 0"
          class="mt-4"
        >
          {{ $t("add_page.no_result") }} {{ query }}.
        </p>
      </div>

      <div v-if="parentTransaction" class="text-muted mb-3">
        <div>
          {{ $t("profitloss.original_buy_transaction", { date: "" }) }}
          {{ parentTransaction.date | toLocaleDateString }}
        </div>
        <div>
          {{ $t("profitloss.quantity") }}
          {{ formatNumber(parentTransaction.holdings, false) }}
          {{ coin.symbol }} ({{ $t("profitloss.left_unsold") }}:
          <b
            >{{ formatNumber(parentTransaction.holdingsLeft, false) }}
            {{ coin.symbol }}</b
          >)
        </div>
        <div>
          {{ $t("profitloss.buy_price") }}
          <b>{{
            formatMoney(
              convertToCurrency(
                parentTransaction.assetPrice,
                null,
                parentTransaction.assetPriceCurrency
              )
            )
          }}</b>
          {{
            parentTransaction.assetPriceCurrency !== assetPriceCurrency
              ? ` (${$t("profitloss.originally")} ` +
                formatMoney(
                  parentTransaction.assetPrice,
                  parentTransaction.assetPriceCurrency
                ) +
                ")"
              : ""
          }}
        </div>
        <div v-if="parentTransaction.notes">
          {{ $t("global.notes") }}: {{ parentTransaction.notes }}
        </div>
      </div>

      <div
        v-if="form.children && form.children.length"
        class="text-muted mb-3"
      >
        <div>
          {{
            $t("profitloss.dependent_transactions", {
              count: form.children.length,
              amount: `${formatNumber(holdingsMin, false)} ${coin.symbol}`,
            })
          }}
        </div>
      </div>

      <b-form v-if="coin" @submit.prevent="onSubmit">
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('profitloss.quantity') + ':'">
              <b-input-group :prepend="coin.symbol">
                <b-form-input
                  type="number"
                  required
                  step="0.00000001"
                  :min="holdingsMin"
                  :max="holdingsMax"
                  :value="editing != 'holdings' ? formatNumber(form.holdings, false) : editingLastText"
                  @input="onInputHoldings"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="'\xA0'">
              <b-form-checkbox
                v-model="form.showInValuation"
                class="mt-2 cursor-pointer"
              >
                {{ $t("profitloss.showInValuation_help") }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="`${purchaseWord} ${$t('exchange.price')}:`"
            >
              <b-input-group :prepend="assetPriceCurrency">
                <b-form-input
                  type="number"
                  :placeholder="`${purchaseWord} ${$t('exchange.price')}`"
                  required
                  step="0.00000001"
                  min="0.00000001"
                  :value="editing != 'assetPrice' ? formatNumber(form.assetPrice, false) : editingLastText"
                  @input="onInputBuyAssetPrice"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('profitloss.value') + ':'">
              <b-input-group :prepend="assetPriceCurrency">
                <b-form-input
                  type="number"
                  :placeholder="$t('profitloss.value')"
                  required
                  step="0.00000001"
                  min="0.00000001"
                  :value="editing != 'sum' ? formatNumber(form.sum, false) : editingLastText"
                  @input="onInputBuySum"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="`${purchaseWord} ${$t('profitloss.date')}`"
            >
              <b-form-input
                v-model="form.date"
                type="date"
                :max="currentDateStr"
                :placeholder="`${purchaseWord} ${$t('profitloss.date')}`"
                :required="!form.date"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('global.notes') + ':'">
              <b-form-textarea
                aria-describedby="transaction_notes-help"
                v-model="form.notes"
                :state="formValidation.notes ? null : false"
                rows="1"
                max-rows="6"
                :placeholder="
                  $t('profitloss.transaction_notes_placeholder')
                "
              ></b-form-textarea>
              <b-form-text
                id="transaction_notes-help"
                class="text-right"
                :text-variant="formValidation.notes ? 'muted' : 'danger'"
              >
                {{ (form.notes || "").length + "/" + notesMaxLength }}
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <div>
          {{ $t("profitloss.profitloss") }}:
          <span
            :class="[
              'PLPTable__pl_colored',
              sellProfitLoss < 0 ? 'PLPTable__pl_colored--Negative' : '',
            ]"
          >
            {{ formatMoney(convertToCurrency(sellProfitLoss)) }}
          </span>
        </div>

        <div class="text-right">
          <b-button type="submit" variant="primary" class="btn btn-primary">
            {{
              pageMode === "edit"
                ? $t("edit_asset_page.save")
                : $t("exchange.add")
            }}
            {{ pageMode === "sell" ? $t("exchange.Sell") : "" }}
            {{ $t("profitloss.transaction") }}
          </b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ShortId from "short-unique-id";
import debounce from "lodash/debounce";
import NumbersMixin from "@/mixins/numbers";
import options from "@/options";
import BackButton from "@/components/BackButton.vue";

const currentDateStr = new Date().toLocaleDateString("en-CA");

export default {
  name: "AddTransaction",
  metaInfo: {
    title: "Add Asset",
  },
  mixins: [NumbersMixin],
  components: {
    BackButton,
  },
  data() {
    return {
      query: "",
      filteredCoinsArr: [],
      editing: "",
      editingLastText: "0",
      coin: null,
      parentTransaction: null,
      holdingsMin: 0.00000001,
      holdingsMax: null,
      form: {
        id: null,
        parent: null,
        exchId: 0,
        currencyId: null,
        holdings: 0,
        assetPrice: 0,
        assetPriceCurrency: null,
        sum: 0, // in assetPriceCurrency
        date: currentDateStr,
        notes: null,
        showInValuation: true,
      },
    };
  },
  computed: {
    transactionId() {
      return this.$route.params.transactionId !== "new"
        ? this.$route.params.transactionId
        : null;
    },
    pageMode() {
      return this.$route.params.pageMode;
    },
    purchaseWord() {
      return this.parentTransaction
        ? this.$t("exchange.Sell")
        : this.$t("exchange.Buy");
    },
    sellProfitLoss() {
      if (this.parentTransaction) {
        return (
          this.form.holdings *
            this.convertToCurrency(
              this.form.assetPrice,
              "USD",
              this.assetPriceCurrency
            ) -
          this.form.holdings *
            this.convertToCurrency(
              this.parentTransaction.assetPrice,
              "USD",
              this.parentTransaction.assetPriceCurrency
            )
        );
      }
      return (
        this.form.holdings * this.coin.price_usd -
        this.form.holdings *
          this.convertToCurrency(
            this.form.assetPrice,
            "USD",
            this.assetPriceCurrency
          )
      );
    },
    assetPriceCurrency() {
      return this.form.assetPriceCurrency || this.currency;
    },
    formValidation() {
      const { form } = this;
      const formValidation = {
        notes: (form.notes || "").length <= this.notesMaxLength,
        isValid: true,
      };
      for (let field in formValidation) {
        formValidation.isValid =
          formValidation.isValid && formValidation[field];
      }
      return formValidation;
    },
    assetsArrRes() {
      return this.assetsArr(null).map((v) => v.coinData);
    },
    filteredCoins() {
      return this.query.length ? this.filteredCoinsArr : this.assetsArrRes;
    },
    ...mapGetters({
      currency: "settings/currency",
      convertToCurrency: "currency/convertToCurrency",
      coinByFindParams: "coinmarketcap/coinByFindParams",
      assetsArr: "profitloss/assetsArr",
      getTransaction: "profitloss/getTransaction",
    }),
  },
  watch: {
    query(val) {
      this.fetchSearchResults(val);
    },
    currency(newVal, oldVal) {
      if (this.form.assetPriceCurrency) {
        return;
      }
      this.form.assetPrice = this.convertToCurrency(
        this.form.assetPrice,
        newVal,
        oldVal
      );
    },
  },
  methods: {
    selectCoin(coin) {
      this.addCoin(coin);
      this.coin = coin;
      this.form.currencyId = coin.id;
      this.form.assetPrice = this.convertToCurrency(coin.price_usd);
      this.form.sum = this.convertToCurrency(
        coin.price_usd * this.form.holdings
      );
    },
    onInputHoldings(value) {
      this.editing = 'holdings';
      return this.onEditingChanged(value);
    },
    onInputBuySum(value) {
      this.editing = 'sum';
      return this.onEditingChanged(value);
    },
    onInputBuyAssetPrice(value) {
      this.editing = 'assetPrice';
      return this.onEditingChanged(value);
    },
    onEditingChanged(value) {
      this.editingLastText = value;
      const valueFloat = parseFloat(value);
      this.form[this.editing] = valueFloat || 0;
      if (!valueFloat) return;

      if (this.editing === 'holdings') {
        if (this.form.assetPrice) {
          this.form.sum = valueFloat * this.form.assetPrice;
        }
      } else if (this.editing === 'sum') {
        if (this.form.assetPrice) {
          this.form.holdings = valueFloat / this.form.assetPrice;
        }
      } else if (this.editing === 'assetPrice') {
        if (this.pageMode === "sell" || !this.form.sum) {
          this.form.sum = valueFloat * this.form.holdings;
        } else {
          this.form.holdings = this.form.sum / valueFloat;
        }
      }
    },
    onSubmit() {
      if (!this.formValidation.isValid) {
        return;
      }
      const form = { ...this.form };
      form.holdings = this.formatNumber(form.holdings, false) * (form.parent ? -1 : 1);
      form.assetPrice = this.formatNumber(form.assetPrice, false);
      form.sum = this.formatNumber(form.sum, false);
      form.notes = form.notes || null;
      if (!form.id) {
        form.id = new ShortId().randomUUID(16);
        form.assetPriceCurrency = this.assetPriceCurrency;
        this.addTransaction(form);
      } else {
        this.updateTransaction(form);
      }
      this.$router.push("/");
    },
    goToFirstIfAvailable() {
      if (this.query.length === 0 || this.filteredCoins.length === 0) {
        return;
      }
      this.selectCoin(this.filteredCoins[0]);
    },
    fetchSearchResults: debounce(function (query) {
      if (!query) {
        this.filteredCoinsArr = [];
        return;
      }
      this.searchRequest(query.trim())
        .then((filteredCoins) => {
          this.filteredCoinsArr = filteredCoins;
        })
        .catch((error) => {
          console.log(error);
        });
    }, 500),
    ...mapActions({
      searchRequest: "coinmarketcap/getCoinMarketCapDataByQuery",
      addCoin: "coinmarketcap/addCoin",
      addTransaction: "profitloss/addTransaction",
      updateTransaction: "profitloss/updateTransaction",
    }),
  },
  created() {
    this.currentDateStr = currentDateStr;
    this.notesMaxLength = options.notesMaxLength;
  },
  mounted() {
    if (this.transactionId) {
      const transaction = { ...this.getTransaction(this.transactionId) };
      const coin = this.coinByFindParams({
        currencyId: transaction.currencyId,
      });
      if (this.pageMode === "sell") {
        if (transaction.parent) {
          this.$router.push("/");
        }
        this.parentTransaction = transaction;
        this.form.parent = transaction.id;
        this.form.currencyId = coin.id;
        this.form.holdings = transaction.holdingsLeft;
        this.form.assetPrice = this.convertToCurrency(coin.price_usd);
        this.form.sum = coin.price_usd * transaction.holdingsLeft;
      } else {
        this.form = transaction;
      }
      if (this.form.parent) {
        this.form.holdings = Math.abs(this.form.holdings);
        if (!this.parentTransaction) {
          this.parentTransaction = { ...this.getTransaction(this.form.parent) };
        }
      }
      this.coin = coin;

      // TODO: take in account history transactions for min holdings range
      if (this.pageMode === "sell") {
        if (this.parentTransaction) {
          this.holdingsMax = this.parseFloat(this.parentTransaction.holdingsLeft, false);
        }
      }
      if (this.pageMode === "edit") {
        if (this.parentTransaction) {
          this.holdingsMax =
            this.parseFloat(this.parentTransaction.holdingsLeft + transaction.holdings, false);
        } else {
          this.holdingsMin = this.parseFloat(transaction.holdings - transaction.holdingsLeft, false);
        }
      }
    }
    this.$refs.input.focus();
  },
};
</script>

<style lang="scss" scoped>
@import "~@/scss/mixins";
@import "~@/scss/variables";

td {
  vertical-align: middle;
  font-size: 18px;
}

em {
  font-style: normal;
  opacity: 0.7;
  font-weight: 300;
}

input {
  padding-left: 25px;
  padding-right: 25px;
  border-radius: 8px;
  font-size: 22px;
  @include breakpoint-manual(max-width, $break-small) {
    padding-left: 15px;
    padding-right: 15px;
  }
}

.PLPTable__pl_colored {
  color: $color-tertiary;

  &--Negative {
    color: $color-primary;
  }
}
</style>